import React, { useState, useEffect } from 'react';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { CopyableAddress, CopyableMint } from '../common/CopyButton';
import QuickTradeActions from '../trading/QuickTradeActions';
import { apiClient } from '../../utils/apiClient';

const AssetsPage = () => {
  const { activeWallet } = useWallet();
  const { showSuccess, showError, showWarning } = useToast();
  const { theme, isDark } = useTheme();

  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, wallet, featured
  const [sortBy, setSortBy] = useState('value'); // value, balance, name

  useEffect(() => {
    console.log('AssetsPage - activeWallet:', activeWallet);
    console.log('AssetsPage - activeWallet type:', typeof activeWallet);
    console.log('AssetsPage - activeWallet keys:', activeWallet ? Object.keys(activeWallet) : 'null');

    if (activeWallet?.wallet_address) {
      console.log('AssetsPage - Fetching assets for wallet:', activeWallet.wallet_address);
      fetchAssets();
    } else if (activeWallet?.address) {
      console.log('AssetsPage - Fetching assets for wallet (address field):', activeWallet.address);
      fetchAssets();
    } else {
      console.log('AssetsPage - No active wallet found, activeWallet:', activeWallet);
      setLoading(false);
    }
  }, [activeWallet]);

  const fetchAssets = async () => {
    const walletAddress = activeWallet?.wallet_address || activeWallet?.address;

    if (!walletAddress) {
      showWarning('Please connect a wallet first');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await apiClient.get(`/assets?wallet_address=${walletAddress}`);

      if (data?.success && data?.data) {
        setAssets(data.data);
      } else {
        showError('Failed to load assets');
        setAssets(null);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      showError(error.message || 'Error loading assets');
      setAssets(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAssets = async () => {
    try {
      await apiClient.post('/assets/refresh', {});
      showSuccess('Balance cache cleared, refreshing...');
      await fetchAssets();
    } catch (error) {
      console.error('Error refreshing assets:', error);
      showError('Failed to refresh assets');
    }
  };

  const filteredAndSortedTokens = () => {
    if (!assets?.tokens || !Array.isArray(assets.tokens)) return [];

    let filtered = [...assets.tokens];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(token =>
        token.token_symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        token.token_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        token.token_address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(token => {
        if (filterType === 'wallet') return token.hasBalance;
        if (filterType === 'featured') return !token.hasBalance;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'value':
          return (b.balance_usd || 0) - (a.balance_usd || 0);
        case 'balance':
          return parseFloat(b.balance || 0) - parseFloat(a.balance || 0);
        case 'name':
          return a.token_symbol.localeCompare(b.token_symbol);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const formatBalance = (balance, decimals = 6) => {
    if (!balance || balance === '0') return '0';
    const num = parseFloat(balance);
    if (num < 0.000001) return '< 0.000001';
    return num.toFixed(decimals);
  };

  const formatUSD = (amount) => {
    if (!amount || amount === 0) return '$0.00';
    if (amount < 0.01) return '< $0.01';
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle trade completion
  const handleTradeComplete = (tradeData) => {
    showSuccess(`Trade completed successfully!`);
    // Refresh assets after trade completion
    fetchAssets();
    console.log('Trade completed:', tradeData);
  };

  const walletAddress = activeWallet?.wallet_address || activeWallet?.address;

  if (!activeWallet || !walletAddress) {
    return (
      <div className="assets-page">
        <div className="no-wallet-message">
          <h2>No Wallet Connected</h2>
          <p>Please connect a wallet to view your assets.</p>
          <div style={{ marginTop: '20px', fontSize: '12px', color: '#666', textAlign: 'left' }}>
            <strong>Debug Info:</strong><br />
            activeWallet: {activeWallet ? JSON.stringify(activeWallet, null, 2) : 'null'}<br />
            localStorage tokens: {localStorage.getItem('tokens') ? 'Found' : 'Not found'}<br />
            Check console for more details.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assets-page modern-assets">
      <div className="assets-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-title"
            style={{
              display: 'flex', alignItems: 'center',
              gap: '10px', marginTop: '17px'
            }}>
            <div className="title-icon">💎</div>
            <h1>My Assets</h1>
            <div className="title-subtitle">Portfolio Overview</div>
          </div>
          <div className="wallet-info">
            <div className="wallet-label">Connected Wallet</div>
            <div className="wallet-address">
              {walletAddress ? (
                <CopyableAddress
                  address={walletAddress}
                  truncate={true}
                  truncateLength={12}
                />
              ) : 'Loading...'}
            </div>
          </div>
        </div>
        <button
          onClick={refreshAssets}
          className="refresh-button modern-refresh"
          disabled={loading}
        >
          <div className="refresh-icon">
            {loading ? '🔄' : '↻'}
          </div>
          <span>Refresh</span>
        </button>
      </div>

      {assets?.summary && (
        <div className="portfolio-summary modern-summary">
          <div className="summary-card modern-card primary-card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>Portfolio Value</h3>
              <div className="value primary-value">{formatUSD(assets.summary.totalValue)}</div>
              <div className="card-subtitle">Total USD Value</div>
            </div>
            <div className="card-glow"></div>
          </div>
          <div className="summary-card modern-card secondary-card">
            <div className="card-icon">🪙</div>
            <div className="card-content">
              <h3>Total Tokens</h3>
              <div className="value secondary-value">{assets.summary.totalTokens}</div>
              <div className="card-subtitle">Unique Assets</div>
            </div>
            <div className="card-glow"></div>
          </div>
          {assets.summary.largestHolding && (
            <div className="summary-card modern-card accent-card">
              <div className="card-icon">👑</div>
              <div className="card-content">
                <h3>Largest Holding</h3>
                <div className="value accent-value">
                  {assets.summary.largestHolding.token_symbol}
                </div>
                <div className="card-subtitle">
                  {formatUSD(assets.summary.largestHolding.balance_usd)}
                </div>
              </div>
              <div className="card-glow"></div>
            </div>
          )}
        </div>
      )}

      <div className="assets-controls modern-controls">
        <div className="search-container modern-search">
          <div className="search-icon">🔍</div>
          <input
            type="text"
            placeholder="Search tokens, symbols, or addresses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input modern-input"
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm('')}
            >
              ✕
            </button>
          )}
        </div>

        <div className="filter-controls modern-filters">
          <div className="filter-group">
            <label className="filter-label">Filter</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select modern-select"
            >
              <option value="all">All Tokens</option>
              <option value="wallet">Wallet Tokens</option>
              <option value="featured">Featured Tokens</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Sort</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select modern-select"
            >
              <option value="value">Sort by Value</option>
              <option value="balance">Sort by Balance</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container modern-loading">
          <div className="loading-spinner modern-spinner">
            <div className="spinner-ring"></div>

          </div>
          <div className="loading-text">Loading your assets...</div>
        </div>
      ) : (
        <div className="assets-list modern-list">
          {filteredAndSortedTokens().length === 0 ? (
            <div className="no-assets-message modern-empty">
              <div className="empty-icon">🔍</div>
              <h3>No tokens found</h3>
              <p>No tokens match your current search criteria.</p>
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            filteredAndSortedTokens().map((token) => (
              <div key={token.token_address}
                className={`asset-item modern-asset ${token.hasBalance ? 'has-balance' : 'featured-only'}`}
              >
                <div className="asset-info modern-info">
                  <div className="token-icon modern-icon">
                    {token.image_url ? (
                      <img src={token.image_url} alt={token.token_symbol} />
                    ) : (
                      <div className="token-placeholder modern-placeholder">
                        {token.token_symbol.charAt(0)}
                      </div>
                    )}
                    <div className="icon-glow"></div>
                  </div>
                  <div className="token-details modern-details">
                    <div className="token-name modern-name">
                      <span className="symbol modern-symbol">{token.token_symbol}</span>
                      <span className="name modern-name-text">{token.token_name}</span>
                      {token.hasBalance && (
                        <div className="balance-indicator">●</div>
                      )}
                    </div>
                    <div className="modern-address">
                      {token.token_address ? (
                        <CopyableMint
                          mintAddress={token.token_address}
                          truncate={true}
                          truncateLength={12}
                        />
                      ) : 'Unknown Address'}
                    </div>
                    {token.hasBalance ? (
                      <>
                        <div className="balance-amount modern-amount">
                          {formatBalance(token.balance)} {token.token_symbol}
                        </div>
                        <div className="balance-usd modern-usd">
                          {formatUSD(token.balance_usd)}
                        </div>
                        {token.price_per_token > 0 && (
                          <div className="token-price modern-price">
                            ${token.price_per_token.toFixed(6)} per token
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="no-balance modern-no-balance">
                        <span className="featured-badge modern-badge">Featured</span>
                        <div className="balance-amount modern-amount">0 {token.token_symbol}</div>
                      </div>
                    )}
                  </div>
                </div>


                <div className="asset-actions modern-actions">
                  {token.website && (
                    <a
                      href={token.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-link modern-action"
                      title="Visit Website"
                    >
                      🌐
                    </a>
                  )}
                  {token.twitter && (
                    <a
                      href={token.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-link modern-action"
                      title="Twitter"
                    >
                      🐦
                    </a>
                  )}
                  {token.telegram && (
                    <a
                      href={token.telegram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-link modern-action"
                      title="Telegram"
                    >
                      📱
                    </a>
                  )}
                </div>

                {/* Quick Trade Actions */}
                <QuickTradeActions
                  token={{
                    mint: token.token_address,
                    symbol: token.token_symbol,
                    name: token.token_name,
                    decimals: token.decimals || 6,
                    logo: token.image_url || '/images/unknown-token.png',
                    price: token.price_per_token || 0,
                    change24h: 0,
                    volume24h: 0,
                    marketCap: 0,
                    description: token.description || '',
                    address: token.token_address
                  }}
                  onTradeComplete={handleTradeComplete}
                />
              </div>
            ))
          )}
        </div>
      )}

      {assets?.lastUpdated && (
        <div className="last-updated modern-updated">
          <div className="updated-icon">🕒</div>
          <span>Last updated: {new Date(assets.lastUpdated).toLocaleString()}</span>
        </div>
      )}
    </div>
  );
};

export default AssetsPage;